import { useCallback } from 'react';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import {
  ExternalFeatureLayer,
  ExternalFeatureLayerName,
  FeatureLayerGeomType,
} from 'features/mapping/mapping.types';
import { ExternalFeatureProperties } from './ExternalFeatureLayer.types';
import { MapboxFeature, useMapLayerEvents } from '../useMapLayerEvents';

type UseLayerClickHandler = (
  features: MapboxFeature<ExternalFeatureProperties>[],
) => void;

const useLayerClickHandler = (
  sourceId: ExternalFeatureLayerName,
  displayName: string,
  geomType: FeatureLayerGeomType,
): UseLayerClickHandler => {
  const { setMapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();

  const clickHandler = useCallback(
    (features: MapboxFeature<ExternalFeatureProperties>[]) => {
      const feature = features[0];

      setMapDrawerEntityForPoi({
        id: feature.id,
        type: sourceId,
        displayName,
        geomType,
      });
    },
    [setMapDrawerEntityForPoi, sourceId, displayName, geomType],
  );

  return clickHandler;
};

export const useExternalFeatureLayerMapEvents = (
  sourceId: ExternalFeatureLayerName,
  featureLayer: ExternalFeatureLayer,
): string | number => {
  const { geomType, displayName, displayStyleData } = featureLayer;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const clickHandler = useLayerClickHandler(sourceId, displayName, geomType);

  if (displayStyleData.displayStyleType !== 'embedded') {
    throw new Error(
      'useLayerClickHandler requires displayStyleType to be embedded',
    );
  }

  useMapLayerEvents<ExternalFeatureProperties>({
    layerId: displayStyleData.clickListenerLayerId,
    onClick: clickHandler,
  });

  const selectedId =
    (mapDrawerEntityForPoi?.type === sourceId && mapDrawerEntityForPoi?.id) ||
    '';
  return selectedId;
};
